import {
  createFileRoute,
  stripSearchParams,
  type ErrorComponentProps,
} from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { newsService } from '@/features/news/api/newsService'
import { useNewsFeed } from '@/features/news/api/queries'
import NewsFeed from '@/features/news/components/NewsFeed'
import { FEED_SKELETON } from '@/features/news/components/news-skeletons'
import NewsFilters from '@/features/news/components/NewsFilters'
import NewsPageTabs from '@/features/news/components/NewsPageTabs'
import { NEWS_FILTER_DEFAULTS, toFeedQuery } from '@/features/news/filters'
import { loadMarketUpdate } from '@/screens/market/MarketUpdateScreen.loader'
import {
  MARKET_UPDATE_SEARCH_DEFAULTS,
  filtersFromSearch,
  marketUpdateSearchSchema,
  searchFromFilters,
} from '@/screens/market/news-search'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title="Market Update" />

/** The tabs, the filter row (search 36, Sort, Filters) and the feed's first day. */
function MarketUpdatePending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading the market update…"
      header={HEADER}
      pageTitle="Market Update"
    >
      <div className="flex flex-col gap-4">
        <NewsPageTabs value="updates" />
        <Skeleton className="h-11 w-full lg:h-9" />
      </div>
      {FEED_SKELETON}
    </ScreenLayout>
  )
}

function MarketUpdateError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn't load the market update"
      description="The data did not come back. Nothing on your device is wrong."
    />
  )
}

export const Route = createFileRoute('/_app/market/update')({
  validateSearch: marketUpdateSearchSchema,
  search: { middlewares: [stripSearchParams(MARKET_UPDATE_SEARCH_DEFAULTS)] },
  loader: loadMarketUpdate,
  pendingComponent: MarketUpdatePending,
  errorComponent: MarketUpdateError,
  component: MarketUpdateScreen,
})

/**
 * Market update's Updates tab (mock `#/market/update`): the exchange's bullet posts, as cards
 * under day labels, behind the Updates | News tabs and the search · Sort · Filters row.
 *
 * mm-be's `/market/updates/` takes `category` (the dialog's Category) and `q` (the search).
 * "Oldest first", Show, Sector and Source are drawn and selectable; picking one shows the no-data
 * state with a way back, and asks nothing (owner ruling 1, 2026-10-28; `asks/nw1.md`).
 */
function MarketUpdateScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const filters = filtersFromSearch(search)
  const query = toFeedQuery(filters, search.sort, search.query)
  const feed = useNewsFeed(query)

  return (
    <ScreenLayout header={HEADER} pageTitle="Market Update">
      <div className="flex flex-col gap-4">
        <NewsPageTabs value="updates" />
        <NewsFilters
          placeholder="Search a company, sector or broker"
          query={search.query ?? ''}
          onQueryChange={(next) => {
            const trimmed = next.trim()
            void navigate({
              search: (prev) => ({ ...prev, query: trimmed === '' ? undefined : trimmed }),
            })
          }}
          sort={search.sort}
          onSortChange={(sort) => {
            void navigate({ search: (prev) => ({ ...prev, sort }) })
          }}
          filters={filters}
          onFiltersChange={(next) => {
            void navigate({
              search: (prev) => ({ ...prev, ...searchFromFilters(next) }),
            })
          }}
          countFor={(draft) => toFeedQuery(draft, search.sort, search.query)}
        />
      </div>

      <NewsFeed
        feed={feed}
        served={newsService.canRequest(query)}
        hasNextPage={feed.hasNextPage}
        isFetchingNextPage={feed.isFetchingNextPage}
        onLoadMore={() => {
          void feed.fetchNextPage()
        }}
        onClearFilters={() => {
          void navigate({
            search: {
              ...MARKET_UPDATE_SEARCH_DEFAULTS,
              ...searchFromFilters(NEWS_FILTER_DEFAULTS),
            },
          })
        }}
      />
    </ScreenLayout>
  )
}
